import { useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  SparkleIcon,
  SpinnerIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  RobotIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TextInput } from '@/components/common/TextInput';
import { orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import {
  useCustomerAgentActionsConfig,
  useThreadAgentActions,
  useWorkflowRunStatus,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionsAgentActions';

interface CollectionThreadAgentActionsPanelProps {
  threadId: string;
  customerId: string;
}

/**
 * Generic "Agent actions" panel for a collection thread. Renders only when the
 * thread's customer has bound portal actions. The agent suggests a relevant
 * action with a generated instruction (the human can edit it); on approve, the
 * bound action-workflow runs and its results land on the thread as evidence.
 *
 * Works for any thread type (short-pay, deduction, …) — it's driven entirely by
 * the customer's configured actions, not the thread's recommendation pipeline.
 */
export function CollectionThreadAgentActionsPanel({
  threadId,
  customerId,
}: CollectionThreadAgentActionsPanelProps) {
  const t = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { config, isLoading } = useCustomerAgentActionsConfig(customerId);
  const { suggest, suggestions, isSuggesting, hasSuggested, run, isRunning } =
    useThreadAgentActions(threadId);

  // Human-edited instruction, keyed by actionId (defaults to the generated one).
  const [editedContext, setEditedContext] = useState<Record<string, string>>(
    {},
  );
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const runStatus = useWorkflowRunStatus(activeRunId);

  // When a run finishes, refresh the thread's evidence + detail so the pulled
  // documents (and any refreshed recommendation) appear.
  useEffect(() => {
    if (!activeRunId || !runStatus.isTerminal) return;
    if (runStatus.status === 'completed') {
      void queryClient.invalidateQueries({
        queryKey:
          orpc.accountsReceivable.arCollectionThreads.listEvidence.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCollectionThreads.get.key(),
      });
      showToast({
        color: 'success',
        title: t('Portal action complete'),
        message: t('The results were filed onto this thread.'),
      });
    } else {
      showToast({
        color: 'error',
        title: t('Portal action failed'),
        message: runStatus.stopReason ?? t('The operator run did not finish.'),
      });
    }
    setActiveRunId(null);
  }, [
    activeRunId,
    runStatus.isTerminal,
    runStatus.status,
    runStatus.stopReason,
    queryClient,
    showToast,
    t,
  ]);

  if (isLoading || !config || config.agentActions.length === 0) {
    return null;
  }

  const contextValue = (actionId: string, fallback: string) =>
    editedContext[actionId] ?? fallback;

  const handleApprove = async (actionId: string, defaultContext: string) => {
    try {
      const result = await run(
        actionId,
        contextValue(actionId, defaultContext),
      );
      setActiveRunId(result.workflowRunId);
    } catch {
      showToast({
        color: 'error',
        title: t('Could not start the portal action'),
      });
    }
  };

  const handleSuggest = async () => {
    try {
      await suggest();
    } catch {
      showToast({
        color: 'error',
        title: t('Could not get portal action suggestions'),
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <RobotIcon className="size-4" weight="duotone" />
          <T>Portal actions</T>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs">
            <T>
              This customer has portal actions the agent can take on your
              behalf.
            </T>
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleSuggest()}
            disabled={isSuggesting}
          >
            {isSuggesting ? (
              <SpinnerIcon className="size-4 animate-spin" />
            ) : (
              <SparkleIcon className="size-4" />
            )}
            <T>Suggest</T>
          </Button>
        </div>

        {hasSuggested && suggestions.length === 0 && (
          <p className="text-muted-foreground text-xs">
            <T>
              The agent didn't find a portal action relevant to this thread.
            </T>
          </p>
        )}

        {suggestions.map((suggestion) => {
          return (
            <div
              key={suggestion.actionId}
              className="space-y-2 rounded-md border p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{suggestion.label}</span>
                {suggestion.recommended && (
                  <Badge variant="secondary" className="text-xs">
                    <T>Recommended</T>
                  </Badge>
                )}
              </div>
              {suggestion.rationale && (
                <p className="text-muted-foreground text-xs">
                  {suggestion.rationale}
                </p>
              )}
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">
                  <T>Instruction</T>
                </label>
                <TextInput
                  className="min-h-16 text-xs"
                  value={contextValue(suggestion.actionId, suggestion.context)}
                  onValueChange={(v) =>
                    setEditedContext((prev) => ({
                      ...prev,
                      [suggestion.actionId]: v,
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    void handleApprove(suggestion.actionId, suggestion.context)
                  }
                  disabled={isRunning || Boolean(activeRunId)}
                >
                  <T>Approve &amp; run</T>
                </Button>
              </div>
            </div>
          );
        })}

        {activeRunId && (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            {runStatus.status === 'completed' ? (
              <CheckCircleIcon className="size-4 text-emerald-600" />
            ) : runStatus.status === 'failed' ? (
              <WarningCircleIcon className="size-4 text-red-600" />
            ) : (
              <SpinnerIcon className="size-4 animate-spin" />
            )}
            <T>Running the portal action…</T>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
